import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

const land = keyframes`
  0%, 18% { opacity: 0; transform: translateY(8px) scale(0.92); }
  36%, 80% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; }
`;

const write = keyframes`
  0%, 10% { width: 0; opacity: 0; }
  28%, 78% { width: var(--w); opacity: 1; }
  100% { width: 0; opacity: 0; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

function MoodboardPreview() {
  const slots = [
    { left: 16, top: 14, w: 58, h: 84, color: "#9a3412", delay: "0s" },
    { left: 80, top: 14, w: 40, h: 40, color: "#c2410c", delay: "0.2s" },
    { left: 80, top: 58, w: 40, h: 40, color: "#44403c", delay: "0.4s" },
  ];
  return (
    <Box
      aria-hidden
      sx={{ position: "relative", height: 112, bgcolor: "#f8f3ee" }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 8,
          right: 8,
          bottom: 8,
          borderRadius: 0.8,
          bgcolor: "#fff",
          border: "1px solid #eadfd4",
        }}
      />
      {slots.map((slot) => (
        <Box
          key={`${slot.left}-${slot.top}`}
          sx={{
            position: "absolute",
            left: slot.left,
            top: slot.top,
            width: slot.w,
            height: slot.h,
            borderRadius: 0.6,
            bgcolor: slot.color,
            animation: `${land} 4.8s ease-in-out infinite`,
            animationDelay: slot.delay,
            ...motionSafe,
          }}
        />
      ))}
    </Box>
  );
}

function ResumenPreview() {
  const lines = ["78%", "92%", "54%"];
  return (
    <Box
      aria-hidden
      sx={{ position: "relative", height: 112, bgcolor: "#f3f7fb" }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 28,
          top: 12,
          width: 84,
          height: 88,
          borderRadius: 0.8,
          bgcolor: "#fff",
          border: "1px solid #d6e4ee",
          px: 1,
          pt: 1.4,
        }}
      >
        {lines.map((width, i) => (
          <Box
            key={width}
            sx={{
              "--w": width,
              height: 6,
              mb: 1,
              borderRadius: 1,
              bgcolor: i === 0 ? "#0369a1" : "#94a3b8",
              animation: `${write} 4.8s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
              ...motionSafe,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/** Qué vas a montar: collage (moodboard/propuesta), acta (resumen) o ficha técnica. */
export default function SkillAgentBoardIntro({ kind = "moodboard" }) {
  const { t } = useTranslation();
  const resumen = kind === "resumen";
  const ficha = kind === "ficha_tecnica" || kind === "ficha";
  const propuesta = kind === "propuesta" || kind === "propuestas";
  const accent = resumen
    ? "#0369a1"
    : ficha
      ? "#0f766e"
      : propuesta
        ? "#7c3aed"
        : "#9a3412";

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: alpha(accent, 0.28),
        bgcolor: alpha(accent, 0.04),
      }}
    >
      <Box sx={{ flex: { sm: "0 0 200px" }, minHeight: 112 }}>
        {resumen ? <ResumenPreview /> : <MoodboardPreview />}
      </Box>
      <Box sx={{ p: 1.5, display: "flex", alignItems: "center" }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {t(
            resumen
              ? "skillAgent.dialog.newResumenLead"
              : ficha
                ? "skillAgent.dialog.newFichaLead"
                : propuesta
                  ? "skillAgent.dialog.newPropuestaLead"
                  : "skillAgent.dialog.newMoodboardLead",
            ficha
              ? {
                  defaultValue:
                    "Ficha técnica con mesas editables. Puedes partir de una carátula del cliente.",
                }
              : propuesta
                ? {
                    defaultValue:
                      "Montas un collage con dibujos de SKETCH / NEW DESIGNS, fotos del meeting o del SEARCH. Luego exportas PDF.",
                  }
                : undefined,
          )}
        </Typography>
      </Box>
    </Stack>
  );
}
